import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, Phone } from "lucide-react";
import { api, COMPANY } from "@/lib/api";

const PART_TYPES = [
  { v: "starter-motor", label: "Starter Motor" },
  { v: "alternator", label: "Alternator" },
  { v: "rebuild-kit", label: "Rebuild Kit" },
  { v: "other", label: "Other / Not sure" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: new Date().getFullYear() - 5,
  part_type: "starter-motor",
  vrn: "",
  message: "",
  product_sku: "",
};

export default function Quote() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...initial, product_sku: params.get("sku") || "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const sku = params.get("sku");
    if (!sku) return;
    const id = window.setTimeout(() => {
      setForm((f) => ({ ...f, product_sku: sku }));
    }, 0);
    return () => window.clearTimeout(id);
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, vehicle_year: parseInt(form.vehicle_year, 10) };
      const { data } = await api.post("/quotes", payload);
      setDone(data);
      toast.success("Quote request submitted — we'll be in touch within 1 working hour.");
    } catch (err) {
      const msg = err?.response?.data?.detail
        ? JSON.stringify(err.response.data.detail).slice(0, 240)
        : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main data-testid="quote-success" className="border-b border-ink-600">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-24 grid grid-cols-12 gap-10 border-x border-ink-600">
          <div className="col-span-12 md:col-span-7">
            <CheckCircle2 size={48} className="text-copper mb-6" strokeWidth={1.5} />
            <div className="font-mono-tech text-[10px] text-copper mb-4">REQUEST RECEIVED · #{done.id.slice(0, 8).toUpperCase()}</div>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">
              Thanks, {done.full_name.split(" ")[0]}.
            </h1>
            <p className="text-steel-300 text-lg max-w-xl mb-8">
              Your request is on the bench. A technician will come back with a
              fixed-price quote within <span className="text-copper font-bold">one working hour</span>
              {" "}during our opening times.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalog" className="btn-copper">Continue Browsing <ArrowRight size={14} /></Link>
              <a href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`} className="btn-ghost">
                <Phone size={14} /> Call {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 border-t md:border-t-0 md:border-l border-ink-600 pt-6 md:pt-0 md:pl-8">
            <div className="font-mono-tech text-[10px] text-copper mb-4">YOUR SUBMISSION</div>
            <dl className="space-y-3 text-sm">
              {[
                ["Vehicle", `${done.vehicle_make} ${done.vehicle_model} (${done.vehicle_year})`],
                ["Part", done.part_type.replace("-", " ")],
                ["Email", done.email],
                ["Phone", done.phone],
                done.vrn && ["VRN", done.vrn],
                done.product_sku && ["Referenced SKU", done.product_sku],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2 border-b border-ink-600 pb-3">
                  <dt className="font-mono-tech text-[10px] text-steel-500 col-span-1">{k}</dt>
                  <dd className="col-span-2 text-steel-200 capitalize">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="quote-page" className="border-b border-ink-600">
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 border-x border-ink-600">
        {/* Left: copy */}
        <aside className="col-span-12 md:col-span-5 p-8 md:p-12 border-b md:border-b-0 md:border-r border-ink-600 bg-ink-800">
          <div className="font-mono-tech text-[10px] text-copper mb-4">/ GET A QUOTE</div>
          <h1 className="font-display text-4xl md:text-5xl leading-[0.95] mb-6">
            Send your VRN.<br />Get a fixed price.
          </h1>
          <p className="text-steel-300 leading-relaxed mb-10">
            Tell us about your vehicle and we&apos;ll match the right starter motor or
            alternator from stock — or build one for you. No obligation, no spam.
          </p>

          <ul className="space-y-5">
            {[
              [Clock, "Replies within 1 working hour", "Mon–Fri 08:00–17:00"],
              [ShieldCheck, "45+ years on the bench", "Trusted by HGV operators, PSV depots, industrial sites and marine yards."],
              [Phone, "Prefer to talk?", `${COMPANY.phone} or WhatsApp`],
            ].map(([Icon, t, b]) => (
              <li key={t} className="flex gap-4">
                <Icon className="text-copper shrink-0 mt-1" size={22} strokeWidth={1.6} />
                <div>
                  <div className="font-display text-lg">{t}</div>
                  <div className="text-steel-400 text-sm">{b}</div>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        {/* Right: form */}
        <form
          onSubmit={submit}
          data-testid="quote-form"
          className="col-span-12 md:col-span-7 p-6 md:p-10 grid grid-cols-2 gap-px bg-ink-600"
        >
          {form.product_sku && (
            <div className="col-span-2 bg-ink-900 p-4 border-l-2 border-copper">
              <div className="font-mono-tech text-[10px] text-copper">REFERENCED PRODUCT</div>
              <div className="text-sm mt-1">SKU {form.product_sku}</div>
            </div>
          )}

          <Field label="Full name *" name="full_name" testid="quote-full-name" value={form.full_name} onChange={set("full_name")} required />
          <Field label="Email *" type="email" name="email" testid="quote-email" value={form.email} onChange={set("email")} required />
          <Field label="Phone *" name="phone" testid="quote-phone" value={form.phone} onChange={set("phone")} required />
          <Field label="Company (optional)" name="company" testid="quote-company" value={form.company} onChange={set("company")} />

          <div className="col-span-2 bg-ink-900 p-4 font-mono-tech text-[10px] text-steel-400 border-t border-b border-ink-600 mt-2">
            VEHICLE DETAILS
          </div>

          <Field label="Make *" name="vehicle_make" testid="quote-make" value={form.vehicle_make} onChange={set("vehicle_make")} required placeholder="e.g., Ford" />
          <Field label="Model *" name="vehicle_model" testid="quote-model" value={form.vehicle_model} onChange={set("vehicle_model")} required placeholder="e.g., Transit 2.2 TDCi" />
          <Field label="Year *" type="number" name="vehicle_year" testid="quote-year" value={form.vehicle_year} onChange={set("vehicle_year")} required min={1950} max={2100} />
          <Field label="VRN (reg) — optional" name="vrn" testid="quote-vrn" value={form.vrn} onChange={set("vrn")} placeholder="AB12 CDE" />

          <div className="col-span-2 bg-ink-900 p-4">
            <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">PART REQUIRED *</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PART_TYPES.map((p) => (
                <button
                  type="button"
                  key={p.v}
                  data-testid={`quote-part-${p.v}`}
                  onClick={() => setForm((f) => ({ ...f, part_type: p.v }))}
                  className={`px-3 py-3 font-mono-tech text-[10px] border transition-colors ${
                    form.part_type === p.v
                      ? "border-copper bg-copper text-ink-900"
                      : "border-ink-600 text-steel-200 hover:border-copper"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-2 bg-ink-900 p-4">
            <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">MESSAGE *</label>
            <textarea
              data-testid="quote-message"
              required
              minLength={4}
              maxLength={2000}
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Symptoms, old part number, engine size, anything that helps us identify the right unit."
              className="w-full bg-ink-800 border border-ink-600 px-3 py-3 outline-none focus:border-copper focus:ring-1 focus:ring-copper text-sm placeholder:text-steel-500 resize-none"
            />
          </div>

          <div className="col-span-2 bg-ink-900 p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="font-mono-tech text-[9px] text-steel-500 max-w-sm">
              By submitting you agree we&apos;ll contact you regarding your enquiry. We don&apos;t share data.
            </div>
            <button
              type="submit"
              data-testid="quote-submit"
              disabled={submitting}
              className="btn-copper disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Submit Quote Request"} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div className="bg-ink-900 p-4">
      <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">{label}</label>
      <input
        {...rest}
        data-testid={testid}
        className="w-full bg-ink-800 border border-ink-600 px-3 py-3 outline-none focus:border-copper focus:ring-1 focus:ring-copper text-sm placeholder:text-steel-500"
      />
    </div>
  );
}
